/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import type { GetWixClientPromise } from './../../_shared/request-service-provider/use-get-wix-client-promise';

import { fromPollServer } from './fromPollServer';
import type { EnrichedPoll, Poll } from './ricos-types';
import type { EnrichedPollServer } from './server-types';
import { toCreatePollPayload, toPollServer } from './toPollServer';

type Question = { question: EnrichedPollServer };

const BASE_URL = '/_serverless/polls-client/v1';

interface PollsServiceInterface {
  fetchPoll: (pollId: string) => Promise<EnrichedPoll>;
  createPoll: (poll: Poll) => Promise<EnrichedPoll>;
  updatePoll: (poll: EnrichedPoll) => Promise<EnrichedPoll>;
  vote: (pollId: string, optionId: string) => Promise<EnrichedPoll>;
  unvote: (pollId: string, optionId: string) => Promise<EnrichedPoll>;
  getVoters: (
    pollId: string,
    optionId: string,
    params: Record<string, string | number>
  ) => Promise<any>;
}

export class PollsService implements PollsServiceInterface {
  requestOptions: { baseURL: string };

  constructor(private getWixClientPromise: GetWixClientPromise) {
    this.requestOptions = { baseURL: BASE_URL };
  }

  fetchPoll: PollsServiceInterface['fetchPoll'] = async pollId => {
    try {
      const wixClient = await this.getWixClientPromise();
      const response = await wixClient.fetchWithAuth(`${BASE_URL}/questions/${pollId}`);
      const question: Question = await response.json();
      return fromPollServer(question.question);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        throw new Error('POLL_NOT_FOUND');
      }
      throw e;
    }
  };

  createPoll: PollsServiceInterface['createPoll'] = async question => {
    const wixClient = await this.getWixClientPromise();
    const response = await wixClient.fetchWithAuth(`${BASE_URL}/questions`, {
      method: 'POST',
      body: JSON.stringify({
        question: toCreatePollPayload(question),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const responseQuestion: Question = await response.json();
    return fromPollServer(responseQuestion.question);
  };

  updatePoll: PollsServiceInterface['updatePoll'] = async question => {
    try {
      const wixClient = await this.getWixClientPromise();
      const response = await wixClient.fetchWithAuth(`${BASE_URL}/questions/${question.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          question: toPollServer(question),
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const responseQuestion: Question = await response.json();
      return fromPollServer(responseQuestion.question);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        throw new Error('POLL_NOT_FOUND');
      }
      throw e;
    }
  };

  vote: PollsServiceInterface['vote'] = async (pollId, optionId) => {
    const wixClient = await this.getWixClientPromise();
    const response = await wixClient.fetchWithAuth(`${BASE_URL}/questions/${pollId}/vote`, {
      method: 'POST',
      body: JSON.stringify({
        optionId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseQuestion: Question = await response.json();
    return fromPollServer(responseQuestion.question);
  };

  unvote: PollsServiceInterface['unvote'] = async (pollId, optionId) => {
    const wixClient = await this.getWixClientPromise();
    const response = await wixClient.fetchWithAuth(`${BASE_URL}/questions/${pollId}/unvote`, {
      method: 'POST',
      body: JSON.stringify({
        optionId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const responseQuestion: Question = await response.json();
    return fromPollServer(responseQuestion.question);
  };

  getVoters: PollsServiceInterface['getVoters'] = async (pollId, optionId, params) => {
    const url = `${BASE_URL}/questions/${pollId}/options/${optionId}/voters`;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value !== 'undefined') {
        searchParams.set(key, value.toString());
      }
    });

    const wixClient = await this.getWixClientPromise();

    return await wixClient
      .fetchWithAuth(`${url}?${searchParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(response => response.json());
  };
}
